<template>
  <div class="graph-d3">
    <div class="graph-row">
      <div class="header-left">
        <ul class="select-unit-type">
          <li
            data-action="display_fund_price"
            :class="[getFundCurrentUnitType == 'price' ? 'active': '']"
            @click="chooseUnitType('price')"
          >ราคา</li>
          <li
            data-action="display_fund_size"
            :class="[getFundCurrentUnitType == 'unit' ? 'active': '']"
            @click="chooseUnitType('unit')"
          >ทรัพย์สินสุทธิ</li>
        </ul>
      </div>
      <div class="header-right"></div>
    </div>
    <div class="graph-row">
      <div class="header-left">
        <h4 class="period">
          {{getFundCurrentPeriod}}
          <span>:</span>
        </h4>
        <span
          class="price"
          :class="{plus: computeColor(perfPerPeriod), minus: !computeColor(perfPerPeriod)}"
        >{{ perfPerPeriod }} ฿</span>
        <span
          class="percent"
          :class="{plus: computeColor(perfPerPeriodPercent), minus: !computeColor(perfPerPeriodPercent)}"
        >({{ perfPerPeriodPercent ? perfPerPeriodPercent + '%' : 'ไม่สามารถคำนวนค่าได้' }})</span>
      </div>
      <div class="header-right">
        <a
          data-action="open_advance_graph"
          class="ctn-graph-professional"
          href="javascript:;"
          @click="callProfessionalGraph"
        >
          <i class="fas fa-chart-bar"></i> กราฟเทคนิค
        </a>
      </div>
    </div>
    <div class="graph-area">
      <svg id="graph-d3-line" width="100%">
        <defs>
          <pattern
            id="pattern1"
            x="10"
            y="10"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
          />
        </defs>
      </svg>
    </div>
    <div class="select-navigation fade-right" ref="periodNavigation">
      <div class="select-period" v-if="getFundPeriod" @scroll="onScroll" ref="periodScroll">
        <period-navigation
          activePosition="top"
          data-action="change_fund_period"
          :arrPeriod="getFundPeriod.range"
          :alias="`period`"
          @select-data="choosePeriod"
          :defaultItem="getFundCurrentPeriod"
          :slidePerPage="7.5"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import * as d3 from "d3"
import PeriodNavigation from "../../../components/miniapp/global/PeriodNavigation.vue"

// Helper Functions
let helpers = {
  TH: d3.timeFormatDefaultLocale({
    decimal: ".",
    thousands: ",",
    grouping: [3],
    currency: ["$", ""],
    dateTime: "%a %b %e %X %Y",
    date: "%d/%m/%Y",
    time: "%H:%M:%S",
    periods: ["AM", "PM"],
    days: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday"
    ],
    shortDays: ["อ", "จ", "อ", "พ", "พฤ", "ศ", "ส"],
    months: [
      "มกราคม",
      "กุมภาพันธ์",
      "มีนาคม",
      "เมษายน",
      "พฤษภาคม",
      "มิถุนายน",
      "กรกฎาคม",
      "สิงหาคม",
      "กันยายน",
      "ตุลาคม",
      "พฤศจิกายน",
      "ธันวาคม"
    ],
    shortMonths: [
      "ม.ค.",
      "ก.พ.",
      "มี.ค.",
      "เม.ย.",
      "พ.ค.",
      "มิ.ย.",
      "ก.ค.",
      "ส.ค.",
      "ก.ย.",
      "ต.ค.",
      "พ.ย.",
      "ธ.ค."
    ]
  }),

  numberFormat: function(num) {
    var parts = num.toString().split(".")
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return parts.join(".")
  },
  parseDate: d3.timeParse("%Y-%m-%d"),
  getDate: d3.timeFormat("%d/%m/%y"),
  colorTheme: pl => (pl >= 0 ? constants.profit : constants.loss)
}

// Constant
let constants = {
  graphMarginPercentage: 0.1,

  // color and styling
  grey: "#B0B0B0",
  profit: "#17c161",
  loss: "#da1616",

  margin: {
    top: 20,
    left: 0,
    right: 0,
    bottom: 20,
    bgTop: 12
  },

  // Focusing
  focusStroke: 1.5,
  lineStroke: 2,
  axisStroke: 2,
  circleRadius: 4.5,

  // Tooltip
  tooltipWidth: 120,
  tooltipHeight: 45,
  driftRightMargin: 10,
  driftTopMargin: 22.5,

  // Tick Styling
  tickMoveFactor: 34
}

export default {
  components: {
    PeriodNavigation
  },
  data() {
    return {
      firstPaint: true
    }
  },
  props: {
    id: {
      type: String,
      default: ""
    }
  },
  computed: {
    perfPerPeriod() {
      if (this.getFundNav && this.getFundNav.length > 0) {
        let firstObj = this.getFundNav[0]
        let lastObj = this.getFundNav[this.getFundNav.length - 1]

        if (this.getFundCurrentUnitType == "price") {
          var diffVal = parseFloat(lastObj.value) - parseFloat(firstObj.value)
          var diffValToFixed = diffVal.toFixed(4)
        } else {
          var diffVal = parseFloat(lastObj.amount) - parseFloat(firstObj.amount)
          var diffValToFixed = diffVal.toFixed(0)
        }

        var parts = diffValToFixed.toString().split(".")
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        let diffValFormat = parts.join(".")

        if (diffVal >= 0) {
          diffValFormat = "+" + diffValFormat
        }

        if (diffValFormat) {
          return diffValFormat
        }
      }
      return 0
    },
    perfPerPeriodPercent() {
      if (this.getFundNav && this.getFundNav.length > 0) {
        let firstObj = this.getFundNav[0]
        let lastObj = this.getFundNav[this.getFundNav.length - 1]

        if (this.getFundCurrentUnitType == "price") {
          var firstVal = parseFloat(firstObj.value)
          var lastVal = parseFloat(lastObj.value)
        } else {
          var firstVal = parseFloat(firstObj.amount)
          var lastVal = parseFloat(lastObj.amount)
        }
        var diffVal = lastVal - firstVal
        var diffValPercent = (diffVal / firstVal) * 100
        if (diffValPercent === Infinity) {
          return false
        }

        var diffValToFixed = diffValPercent.toFixed(2)

        if (diffVal >= 0) {
          var diffValToFixed = "+" + diffValToFixed
        }

        if (diffValToFixed) {
          return diffValToFixed
        }
      }
      return 0
    },
    checkNavData() {
      return this.getFundNav.length > 0
    },
    ...mapGetters("fund", [
      "getFundNav",
      "getFundCurrentUnitType",
      "getFundCurrentPeriod",
      "getFundPeriod"
    ])
  },
  watch: {
    getFundNav: "renderGraph",
    async id(id) {
      await this.queryData(id)
    }
  },
  async mounted() {
    await this.queryData(this.id)
  },

  methods: {
    ...mapActions("fund", [
      "findFundNav",
      "findFundNavVerify",
      "saveFundPeriod",
      "saveFundUnitType"
    ]),
    async queryData(id) {
      await this.findFundNavVerify(id)
      await this.getNavData()
      await this.renderGraph()
    },
    onScroll() {
      let periodScroll = this.$refs.periodScroll
      let periodList = this.$refs.periodList
      if (periodScroll && periodScroll.scrollLeft < 0) {
        this.$refs.periodNavigation.classList.remove("fade-left")
        this.$refs.periodNavigation.classList.add("fade-right")
      } else if (periodScroll && periodScroll.scrollLeft > 0) {
        this.$refs.periodNavigation.classList.add("fade-right")
        this.$refs.periodNavigation.classList.add("fade-left")
      } else {
        this.$refs.periodNavigation.classList.add("fade-right")
        this.$refs.periodNavigation.classList.remove("fade-left")
      }
      if (
        periodList.offsetWidth - periodScroll.offsetWidth ==
        periodScroll.scrollLeft
      ) {
        this.$refs.periodNavigation.classList.remove("fade-right")
      }
    },
    callProfessionalGraph() {
      this.$emit("call-tradingview", true)
    },
    async getNavData() {
      let obj = {
        fund_id: this.id,
        period: this.getFundCurrentPeriod,
        type: this.getFundCurrentUnitType
      }
      await this.findFundNav(obj)
    },
    async choosePeriod(period) {
      await this.saveFundPeriod(period)
      await this.getNavData()
    },
    async chooseUnitType(type) {
      await this.saveFundUnitType(type)
      await this.getNavData()
    },
    computeColor(num) {
      if (num) {
        if (num.includes("-")) {
          return false
        } else {
          return true
        }
      }
    },
    graphHeight() {
      let graphWidth = document.getElementById("graph-d3-line").clientWidth
      let graphRatio = graphWidth > 760 ? 0.5 : 0.6

      return graphWidth * graphRatio
    },
    renderGraph() {
      if (!this.checkNavData) {
        return
      }

      if (this.firstPaint) {
        this.drawGraph({ isFirst: true })
        this.firstPaint = false
      } else {
        this.drawGraph({ isFirst: false })
      }
    },
    drawGraph(options) {
      let isFirst = options.isFirst
      const unitType = this.getFundCurrentUnitType
      // Preparing Data
      let formattedLineData = this.getFundNav
      let pl =
        this.getFundNav[this.getFundNav.length - 1].v - this.getFundNav[0].v

      let vis = d3.select("#graph-d3-line")
      let spaceNavLineElem = document.getElementById("graph-d3-line")

      // Prepare range and domain
      let svgWidth, svgHeight
      let xRange, yRange
      let xAxis, yAxis
      let xMin = d3.min(formattedLineData, d => d.d)
      let xMax = d3.max(formattedLineData, d => d.d)
      let yMin = d3.min(formattedLineData, d => Number(d.v))
      let yMax = d3.max(formattedLineData, d => Number(d.v))

      //manage text in ticks
      let formatCurrency = function(d) {
        if (unitType == "price") {
          return d.toFixed(2)
        } else {
          let formattedValue = formatValue(d)
          return formattedValue
            .replace("M", " ล้าน")
            .replace("k", ",000")
            .replace("G", " พันล้าน")
        }
      }

      let sizeUpdate = () => {
        svgWidth = document.getElementById("graph-d3-line").clientWidth
        svgHeight = this.graphHeight()
        spaceNavLineElem.style.height = svgHeight + "px"
      }

      let rangeUpdate = () => {
        xRange = d3
          .scaleTime()
          .range([constants.margin.left, svgWidth - constants.margin.right])
          .domain(d3.extent(formattedLineData, d => d.d))
          .domain([
            formattedLineData[0].d,
            formattedLineData[formattedLineData.length - 1].d
          ])

        let graphMargin = (yMax - yMin) * constants.graphMarginPercentage
        yRange = d3
          .scaleLinear()
          .range([constants.margin.bottom, svgHeight - constants.margin.top])
          .domain([yMax + graphMargin, yMin - graphMargin])
      }

      let axisUpdate = () => {
        xAxis = d3
          .axisBottom(xRange)
          .tickFormat(helpers.TH.format("%b %Y"))
          .tickValues([xMin, xMax])
          .tickSize([0])

        yAxis = d3
          .axisRight(yRange)
          .tickFormat(formatCurrency)
          .ticks(3)
      }

      sizeUpdate()
      rangeUpdate()
      axisUpdate()

      // Appending or Changing Element
      let lineGen = d3
        .line()
        .curve(d3.curveLinear) // http://bl.ocks.org/d3indepth/b6d4845973089bc1012dec1674d3aff8
        .x(d => xRange(d.d))
        .y(d => yRange(d.v))

      var formatValue = d3.format(".2s")

      // y Axis
      var make_y_axis = function() {
        return d3.axisLeft(yRange).ticks(3)
      }

      if (isFirst) {
        vis
          .append("svg:g")
          .attr("class", "x axis")
          .call(xAxis)

        vis
          .append("svg:g")
          .attr("class", "y axis")
          .attr("transform", "translate(-20, 0)")
          .attr("fill", "none")
          .call(yAxis)

        vis
          .append("g")
          .attr("class", "grid")
          .style("opacity", ".2")
          .call(
            make_y_axis()
              .tickSize(0 - svgWidth, 0, 0)
              .tickFormat("")
          )

        // Data Line
        vis
          .append("svg:path")
          .attr("class", "line-data")
          .transition()
          .duration(100)
          .attr("d", lineGen(formattedLineData))
          .attr("stroke", helpers.colorTheme(pl))
          .attr("stroke-width", constants.lineStroke)
          .attr("fill", "none")

        // Line on focus
        vis
          .append("line")
          .attr("class", "focus-line")
          .attr("y1", constants.margin.top)
          .attr("y2", svgHeight - constants.margin.bottom)
          .attr("stroke-width", constants.focusStroke)
          .attr("class", "focus-line")
          .attr("stroke", "#999999")
          .style("display", "none")

        // Circle
        let focusCircle = vis
          .append("g")
          .attr("class", "focus-circle")
          .style("display", "none")

        // Circle Background
        focusCircle
          .append("circle")
          .attr("r", constants.circleRadius + 2)
          .attr("fill", "white")

        // Circle Inside
        focusCircle
          .append("circle")
          .attr("class", "inner-circle")
          .attr("r", constants.circleRadius)

        // Tooltip
        let tooltip = vis
          .append("g")
          .attr("class", "focus-tooltip")
          .style("display", "none")

        // Tooltip background
        tooltip
          .append("rect")
          .attr("class", "tooltip-rect")
          .attr("fill-opacity", "0.85")
          .attr("width", constants.tooltipWidth)
          .attr("height", constants.tooltipHeight)

        // Tooltip nav data
        tooltip
          .append("text")
          .attr("x", 10)
          .attr("y", 20)
          .attr("class", "tooltip-nav")
          .attr("text-anchor", "left")
          .attr("fill", "white")
          .style("font-size", "14px")
          .text("10.2464")

        // Tooltip date
        tooltip
          .append("text")
          .attr("class", "tooltip-date")
          .attr("x", 25)
          .attr("y", 35)
          .attr("text-anchor", "left")
          .attr("fill", constants.grey)
          .style("font-size", "12px")
          .text("12/03/07")

        // Tooltip icon
        tooltip
          .append("text")
          .attr("class", "tooltip-logo")
          .attr("x", 12)
          .attr("y", 35)
          .attr("fill", constants.grey)
          .attr("font-family", '"Font Awesome 5 Pro"')
          .attr("font-size", "12px")
          .text("\uf017")
      } else {
        // X Axis
        vis.select(".x").call(xAxis)

        vis.selectAll(".focus-line").style("display", "none")

        vis.selectAll(".focus-circle").style("display", "none")

        vis.selectAll(".focus-tooltip").style("display", "none")

        // y Axis
        vis
          .select(".y")
          .attr("fill", "none")
          .call(yAxis)

        // grid
        vis
          .select(".grid")
          .attr("class", "grid")
          .style("opacity", ".2")
          .call(
            make_y_axis()
              .tickSize(0 - svgWidth, 0, 0)
              .tickFormat("")
          )

        // Data Line
        vis
          .select(".line-data")
          .transition()
          .duration(500)
          .attr("d", lineGen(formattedLineData))
          .attr("stroke", helpers.colorTheme(pl))
          .attr("stroke-width", constants.lineStroke)
          .attr("fill", "none")
      }

      d3.selectAll(".domain").style("display", "none")

      // Handling mouse event for focus tooltip

      //cache for performance
      let elemFocusLine = vis.select(".focus-line")
      let elemFocusCircle = vis.select(".focus-circle")
      let elemFocusTooltip = vis.select(".focus-tooltip")
      let elemFocusTooltipNav = vis.select(".tooltip-nav")
      let elemFocusTooltipDate = vis.select(".tooltip-date")

      // Helpers function
      let mouseEvent = {
        bisectDate: () =>
          d3.bisector(function(d) {
            return d.d
          }).left,
        mouseOver: () => {
          elemFocusLine.style("display", null)
          elemFocusCircle.style("display", null)
          elemFocusTooltip.style("display", null)
        },
        mouseOut: () => {
          elemFocusLine.style("display", "none")
          elemFocusCircle.style("display", "none")
          elemFocusTooltip.style("display", "none")
        },
        mouseMove: function() {
          mouseEvent.handleMove(d3.mouse(this)[0], d3.mouse(this)[1])
        },
        touchMove: function() {
          mouseEvent.handleMove(d3.touches(this)[0][0], d3.touches(this)[0][1])
        },
        handleMove: (xPos, yPos) => {
          elemFocusLine.style("display", null)
          elemFocusCircle.style("display", null)
          elemFocusTooltip.style("display", null)

          let x0 = xRange.invert(xPos),
            i = mouseEvent.bisectDate()(formattedLineData, x0, 1),
            d0 = formattedLineData[i - 1],
            d1 = formattedLineData[i]

          if (d0 != null && d1 != null) {
            let d = x0 > d0.d ? d1 : d0
            if (xRange(d0.d) == 0 && xRange(x0) < xRange(d1.d) / 2) {
              d = d0
            }

            elemFocusLine.attr(
              "transform",
              "translate(" + xRange(d.d) + "," + 0 + ")"
            )
            elemFocusCircle.attr(
              "transform",
              "translate(" + xRange(d.d) + "," + yRange(d.v) + ")"
            )

            // Tooltip move
            // Modify padding for displaying
            let xPad = 0,
              yPad = 0
            if (svgWidth - xRange(d.d) < constants.tooltipWidth) {
              xPad = -constants.tooltipWidth - constants.driftRightMargin * 2
            }

            if (
              svgHeight -
                constants.margin.bottom -
                constants.margin.bgTop -
                yRange(d.v) <
              constants.tooltipHeight
            ) {
              yPad = -constants.tooltipHeight / 2
            }

            if (yRange(d.v) < constants.tooltipHeight) {
              yPad = constants.tooltipHeight / 2
            }

            elemFocusTooltipNav.text(helpers.numberFormat(d.v))
            elemFocusTooltipDate.text(helpers.getDate(d.d))

            // elemFocusTooltip.attr("transform", "translate(" + xRange(d.d+xPad) + "," + yRange(d.v+yPad) + ")")
            elemFocusTooltip.attr(
              "transform",
              "translate(" +
                (xRange(d.d) + xPad + constants.driftRightMargin) +
                "," +
                (yRange(d.v) + yPad - constants.driftTopMargin) +
                ")"
            )
          }
        }
      }

      if (!isFirst) {
        vis.select(".focusing-overlay").remove()
      }

      // Overlay Element
      vis
        .append("rect")
        .attr("class", "focusing-overlay")
        .attr("fill", "none")
        .attr("pointer-events", "all")
        .attr("y", constants.margin.bgTop)
        .attr("width", svgWidth)
        .attr(
          "height",
          svgHeight - constants.margin.bottom - constants.margin.bgTop
        )
        .on("mouseover", mouseEvent.mouseOver)
        .on("mouseout", mouseEvent.mouseOut)
        .on("mousemove", mouseEvent.mouseMove)
        .on("touchmove", mouseEvent.touchMove, true)

      // Styling
      // First and last tick
      let tickUpdate = () => {
        vis
          .selectAll(".tick text")
          .attr("x", constants.tickMoveFactor)
          .attr("y", 10)
          .attr("font-family", '"DBHeaventRez"')
          .attr("font-size", "14px")
          .attr("fill", constants.grey)

        vis
          .selectAll(".x .tick:nth-of-type(2) text")
          .attr("x", "-" + constants.tickMoveFactor)

        vis.selectAll(".x .tick text").attr("y", this.graphHeight() + 20)

        if (svgWidth < 450) {
          var fontSize = "12px"
        } else {
          var fontSize = "16px"
        }
        vis
          .selectAll(".y .tick text")
          .style("font-size", fontSize)
          .style("font-size", "14px")

        vis
          .selectAll(".x-axis-line")
          .attr("x1", 0)
          .attr("x2", svgWidth)
          .attr("y1", svgHeight)
          .attr("y2", svgHeight)

        vis
          .selectAll(".focus-line")
          .attr("y1", constants.margin.top)
          .attr("y2", svgHeight)

        document.getElementById("graph-d3-line").style.height =
          this.graphHeight() + 30 + "px"
      }

      tickUpdate()

      // Inner circle focusing
      vis.select(".inner-circle").attr("fill", helpers.colorTheme(pl))

      // Responsive Event for graph and axis
      let updateAxisAndGraph = () => {
        vis.select(".x").call(xAxis)

        vis
          .select(".y")
          .attr("fill", "none")
          .attr("transform", "translate(-20, 0)")
          .call(yAxis)

        vis.select(".line-data").attr("d", lineGen(formattedLineData))

        vis
          .select(".grid")
          .attr("class", "grid")
          .style("opacity", ".2")
          .call(
            make_y_axis()
              .tickSize(0 - svgWidth, 0, 0)
              .tickFormat("")
          )
      }

      if (!isFirst) {
        d3.select(window).on("resize", null)
      }

      d3.select(window).on("resize", () => {
        sizeUpdate()
        rangeUpdate()
        axisUpdate()
        updateAxisAndGraph()
        tickUpdate()
      })
    }
  }
}
</script>
<style>
</style>

<style lang="scss" scoped>
.graph-d3 {
  .graph-row {
    @extend %container-style;
    margin-bottom: 16px;
    .header-left {
      @include finCol(7, 12, 0px, middle, true, true);
      .select-unit-type {
        list-style: none;
        margin: 0;
        padding: 0;
        position: relative;
        display: inline-block;
        &:after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          height: 4px;
          width: 100%;
          background-color: $color-light-grey;
        }
        li {
          display: inline-block;
          @extend %font-size-s;
          padding: 13px 8px;
          color: $color-secondary-text;
        }
        .active {
          position: relative;
          color: $color-normal-text;
          &:after {
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            height: 4px;
            width: 100%;
            background-color: $color-normal-text;
            z-index: 1;
          }
        }
      }
    }
    .header-right {
      @include finCol(5, 12, 0px, middle, true, true);
      text-align: right;
    }
  }
  .period {
    display: inline-block;
    @extend %font-size-s;
    font-weight: normal;
  }
  .price {
    margin-left: 5px;
    display: inline-block;
    @extend %font-size-s;
  }
  .percent {
    margin-left: 5px;
    display: inline-block;
    @extend %font-size-s;
  }
  .ctn-graph-professional {
    @include finRippleBtn(#f4f4f4, #f4f4f4, $color-normal-text);
    @extend %font-size-ss;
    padding: 5px 19px;
    text-decoration: none;
    font-weight: bold;
    border-radius: 4px;
  }
  .select-navigation {
    position: relative;
    .select-period {
      margin: 20px 0;
      background: #f9f9f9;
      overflow: auto;
      .period-list {
        list-style: none;
        padding: 0;
        margin: 0;
        li {
          display: inline-block;
          max-width: 50px;
          width: 100%;
          @extend %font-size-ss;
          color: $color-normal-text;
          text-align: center;
          padding: 8px 0;
        }
        .active {
          position: relative;
          font-weight: bold;
          &:after {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            height: 3px;
            width: 100%;
            background-color: $color-normal-text;
            z-index: 1;
          }
        }
      }
    }
  }
  .fade-left {
    &:before {
      content: "";
      width: 50px;
      height: 100%;
      z-index: 1;
      position: absolute;
      left: 0;
      top: 0;
      pointer-events: none;
      /* Permalink - use to edit and share this gradient: https://colorzilla.com/gradient-editor/#ffffff+0,ffffff+100&1+0,0+100;White+to+Transparent */
      background: -moz-linear-gradient(
        left,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        left,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#ffffff', endColorstr='#00ffffff',GradientType=1 ); /* IE6-9 */
    }
  }
  .fade-right {
    &:after {
      content: "";
      width: 50px;
      height: 100%;
      z-index: 1;
      position: absolute;
      right: 0;
      top: 0;
      pointer-events: none;
      /* Permalink - use to edit and share this gradient: https://colorzilla.com/gradient-editor/#ffffff+0,ffffff+100&0+0,1+100 */
      background: -moz-linear-gradient(
        left,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        left,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#00ffffff', endColorstr='#ffffff',GradientType=1 ); /* IE6-9 */
    }
  }
}
.tick {
  color: $color-border !important;
}
</style>